import React, { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
	Calendar,
	Heart,
	MessageSquare,
	Search,
	Users,
	Clock,
	Baby,
	MapPin,
	DollarSign,
	ArrowRight,
	Camera,
	Upload,
	Video,
	CheckCircle,
	XCircle,
	AlertCircle,
	CalendarDays,
	Clock3,
	Pencil,
	User,
	UserCircle,
	ChevronLeft,
	ChevronRight,
} from "lucide-react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/lib/supabase";
import { ParentProfile, Conversation, Transaction } from "@/types";
import { VirtualMeeting } from "@/components/messaging/VirtualMeeting";

interface Appointment {
	id: string;
	doula_user_id: string;
	doula_listing_id: string;
	appointment_date: string;
	status: "pending" | "accepted" | "declined" | "cancelled" | "completed";
	duration_minutes: number;
	meeting_link?: string;
	meeting_id?: string;
	notes?: string;
	doula_user: {
		id: string;
		full_name: string;
		avatar_url: string;
		email: string;
	};
	doula_listing: {
		id: string;
		title: string;
		bio: string;
		location: string;
	};
}

interface DashboardData {
	parentProfile: ParentProfile | null;
	favoritesCount: number;
	conversationsCount: number;
	appointmentsCount: number;
	conversations: Conversation[];
	transactions: Transaction[];
	appointments: Appointment[];
	weeksPregnant: number;
	weeksRemaining: number;
}

const ParentDashboard: React.FC = () => {
	const { profile: userProfile } = useAuth();
	const navigate = useNavigate();
	const [loading, setLoading] = useState(true);
	const [uploadingAvatar, setUploadingAvatar] = useState(false);
	const [selectedAppointment, setSelectedAppointment] =
		useState<Appointment | null>(null);
	const [showVirtualMeeting, setShowVirtualMeeting] = useState(false);
	const [showEditDueDate, setShowEditDueDate] = useState(false);
	const [newDueDate, setNewDueDate] = useState("");
	const [savingDueDate, setSavingDueDate] = useState(false);
	const [showDatePicker, setShowDatePicker] = useState(false);
	const [currentDate, setCurrentDate] = useState(new Date());
	const datePickerRef = useRef<HTMLDivElement>(null);
	const [dashboardData, setDashboardData] = useState<DashboardData>({
		parentProfile: null,
		favoritesCount: 0,
		conversationsCount: 0,
		appointmentsCount: 0,
		conversations: [],
		transactions: [],
		appointments: [],
		weeksPregnant: 0,
		weeksRemaining: 0,
	});

	useEffect(() => {
		if (userProfile) {
			fetchDashboardData();
		}
	}, [userProfile]);

	// Close date picker when clicking outside
	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (
				datePickerRef.current &&
				!datePickerRef.current.contains(event.target as Node)
			) {
				setShowDatePicker(false);
			}
		};

		document.addEventListener("mousedown", handleClickOutside);
		return () => document.removeEventListener("mousedown", handleClickOutside);
	}, []);

	const fetchDashboardData = async () => {
		if (!userProfile) return;

		try {
			setLoading(true);

			// Get auth session for edge function calls
			const {
				data: { session },
			} = await supabase.auth.getSession();
			if (!session) {
				console.error("No session found");
				return;
			}

			const headers = {
				"Content-Type": "application/json",
				Authorization: `Bearer ${session.access_token}`,
			};

			// Fetch parent profile using edge function
			const profileResponse = await fetch(
				`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/get-parent-profile`,
				{
					method: "POST",
					headers,
					body: JSON.stringify({ user_id: userProfile.id }),
				},
			);
			const profileResult = await profileResponse.json();
			const parentProfile = profileResult.success
				? profileResult.data.parent_profile
				: null;

			// Fetch appointments using edge function
			const appointmentsResponse = await fetch(
				`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/get-parent-appointments`,
				{
					method: "POST",
					headers,
					body: JSON.stringify({ parentUserId: userProfile.id }),
				},
			);
			const appointmentsResult = await appointmentsResponse.json();
			const appointments = appointmentsResult.data || [];

			// Fetch favorites using edge function
			const favoritesResponse = await fetch(
				`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/get-favorites`,
				{
					method: "POST",
					headers,
					body: JSON.stringify({ parent_user_id: userProfile.id }),
				},
			);
			const favoritesResult = await favoritesResponse.json();
			const favoritesCount = favoritesResult.data?.length || 0;

			// Fetch conversations using edge function
			const conversationsResponse = await fetch(
				`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/manage-conversations`,
				{
					method: "POST",
					headers,
					body: JSON.stringify({
						action: "get_conversations",
						parentUserId: userProfile.id,
					}),
				},
			);
			const conversationsResult = await conversationsResponse.json();
			const conversationsCount = conversationsResult.data?.length || 0;

			// Fetch transactions using edge function
			const transactionsResponse = await fetch(
				`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/get-parent-transactions`,
				{
					method: "POST",
					headers,
					body: JSON.stringify({ parent_user_id: userProfile.id }),
				},
			);
			const transactionsResult = await transactionsResponse.json();
			const transactions = transactionsResult.success
				? transactionsResult.data.transactions
				: [];

			// Calculate pregnancy weeks - Fixed calculation with timezone handling
			let weeksPregnant = 0;
			let weeksRemaining = 0;
			if (parentProfile?.due_date) {
				// Parse the due date manually to avoid timezone issues
				const [year, month, day] = parentProfile.due_date
					.split("-")
					.map(Number);
				const dueDate = new Date(year, month - 1, day); // month is 0-indexed

				const today = new Date();
				const millisecondsPerWeek = 1000 * 60 * 60 * 24 * 7;
				const pregnancyDurationWeeks = 40;

				// Calculate weeks from conception (40 weeks before due date)
				const conceptionDate = new Date(
					dueDate.getTime() - pregnancyDurationWeeks * millisecondsPerWeek,
				);
				const weeksSinceConception = Math.floor(
					(today.getTime() - conceptionDate.getTime()) / millisecondsPerWeek,
				);

				weeksPregnant = Math.max(
					0,
					Math.min(weeksSinceConception, pregnancyDurationWeeks),
				);
				weeksRemaining = Math.max(0, pregnancyDurationWeeks - weeksPregnant);
			}

			setDashboardData({
				parentProfile: parentProfile,
				favoritesCount,
				conversationsCount,
				appointmentsCount: appointments.length,
				conversations: [],
				transactions: transactions || [],
				appointments,
				weeksPregnant,
				weeksRemaining,
			});
		} catch (error) {
			console.error("Error fetching dashboard data:", error);
		} finally {
			setLoading(false);
		}
	};

	const handleAvatarUpload = async (
		event: React.ChangeEvent<HTMLInputElement>,
	) => {
		const file = event.target.files?.[0];
		if (!file || !userProfile) return;

		try {
			setUploadingAvatar(true);

			// Upload to Supabase Storage
			const fileExt = file.name.split(".").pop();
			const fileName = `${userProfile.id}-${Date.now()}.${fileExt}`;
			const { data, error } = await supabase.storage
				.from("avatars")
				.upload(fileName, file);

			if (error) throw error;

			// Get public URL
			const {
				data: { publicUrl },
			} = supabase.storage.from("avatars").getPublicUrl(fileName);

			// Update user profile
			const { error: updateError } = await supabase
				.from("users")
				.update({ avatar_url: publicUrl })
				.eq("id", userProfile.id);

			if (updateError) throw updateError;

			// Refresh dashboard data
			fetchDashboardData();
		} catch (error) {
			console.error("Error uploading avatar:", error);
		} finally {
			setUploadingAvatar(false);
		}
	};

	const handleDueDateEdit = () => {
		const currentDueDate = dashboardData.parentProfile?.due_date;
		if (currentDueDate) {
			setNewDueDate(currentDueDate);
			setCurrentDate(new Date(currentDueDate));
		}
		setShowEditDueDate(true);
	};

	const handleDueDateSave = async () => {
		if (!userProfile || !newDueDate) return;

		try {
			setSavingDueDate(true);

			// Get auth session for edge function call
			const {
				data: { session },
			} = await supabase.auth.getSession();
			if (!session) {
				console.error("No session found");
				return;
			}

			const requestBody = {
				user_id: userProfile.id,
				parent_profile_data: {
					due_date: newDueDate,
				},
			};

			const response = await fetch(
				`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/update-parent-profile`,
				{
					method: "POST",
					headers: {
						"Content-Type": "application/json",
						Authorization: `Bearer ${session.access_token}`,
					},
					body: JSON.stringify(requestBody),
				},
			);

			const result = await response.json();

			if (result.success) {
				setShowEditDueDate(false);
				setShowDatePicker(false);
				fetchDashboardData(); // Refresh data
			} else {
				console.error("Error updating due date:", result.error);
			}
		} catch (error) {
			console.error("Error saving due date:", error);
		} finally {
			setSavingDueDate(false);
		}
	};

	const calculateProgress = () => {
		return Math.min((dashboardData.weeksPregnant / 40) * 100, 100);
	};

	const formatDueDate = (dateString: string) => {
		// Parse the ISO date string manually to avoid timezone issues
		const [year, month, day] = dateString.split("-").map(Number);
		const date = new Date(year, month - 1, day); // month is 0-indexed

		return date.toLocaleDateString("en-US", {
			weekday: "long",
			year: "numeric",
			month: "long",
			day: "numeric",
		});
	};

	const formatDate = (dateString: string) => {
		// Parse the ISO date string manually to avoid timezone issues
		const [year, month, day] = dateString.split("-").map(Number);
		const date = new Date(year, month - 1, day); // month is 0-indexed

		return date.toLocaleDateString("en-US", {
			month: "short",
			day: "numeric",
			year: "numeric",
		});
	};

	// Date picker helper functions
	const formatISODate = (date: Date) => {
		// Get the local date components to avoid timezone conversion
		const year = date.getFullYear();
		const month = String(date.getMonth() + 1).padStart(2, "0"); // month is 0-indexed
		const day = String(date.getDate()).padStart(2, "0");
		return `${year}-${month}-${day}`;
	};

	const formatDisplayDate = (dateString: string) => {
		if (!dateString) return "Select due date";

		// Parse the ISO date string correctly to avoid timezone issues
		const [year, month, day] = dateString.split("-").map(Number);
		const date = new Date(year, month - 1, day); // month is 0-indexed

		return date.toLocaleDateString("en-US", {
			month: "long",
			day: "numeric",
			year: "numeric",
		});
	};

	const getDaysInMonth = (date: Date) => {
		return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
	};

	const getFirstDayOfMonth = (date: Date) => {
		return new Date(date.getFullYear(), date.getMonth(), 1).getDay();
	};

	const handleDateSelect = (day: number) => {
		const selectedDate = new Date(
			currentDate.getFullYear(),
			currentDate.getMonth(),
			day,
		);
		const isoDate = formatISODate(selectedDate);
		setNewDueDate(isoDate);
		setShowDatePicker(false);
	};

	const navigateMonth = (direction: "prev" | "next") => {
		setCurrentDate((prev) => {
			const newDate = new Date(prev);
			if (direction === "prev") {
				newDate.setMonth(prev.getMonth() - 1);
			} else {
				newDate.setMonth(prev.getMonth() + 1);
			}
			return newDate;
		});
	};

	const getLocationDisplay = () => {
		if (!dashboardData.parentProfile) return "Not set";
		const { birth_location_city, birth_location_state } =
			dashboardData.parentProfile;
		if (birth_location_city && birth_location_state) {
			return `${birth_location_city}, ${birth_location_state}`;
		}
		return "Not set";
	};

	const formatAppointmentDate = (dateString: string) => {
		const date = new Date(dateString);
		return date.toLocaleDateString("en-US", {
			weekday: "short",
			month: "short",
			day: "numeric",
			year: "numeric",
		});
	};

	const formatAppointmentTime = (dateString: string) => {
		const date = new Date(dateString);
		return date.toLocaleTimeString("en-US", {
			hour: "numeric",
			minute: "2-digit",
			hour12: true,
		});
	};

	const getTimeUntilAppointment = (dateString: string) => {
		const appointmentDate = new Date(dateString);
		const now = new Date();
		const diffMs = appointmentDate.getTime() - now.getTime();
		const diffMins = Math.floor(diffMs / (1000 * 60));

		if (diffMins < 0) {
			return "Past";
		} else if (diffMins < 60) {
			return `${diffMins} min`;
		} else if (diffMins < 1440) {
			const hours = Math.floor(diffMins / 60);
			return `${hours}h ${diffMins % 60}m`;
		} else {
			const days = Math.floor(diffMins / 1440);
			return `${days}d`;
		}
	};

	const getStatusColor = (status: string) => {
		switch (status) {
			case "completed":
				return "bg-green-100 text-green-800";
			case "accepted":
				return "bg-blue-100 text-blue-800";
			case "pending":
				return "bg-yellow-100 text-yellow-800";
			case "declined":
				return "bg-red-100 text-red-800";
			case "cancelled":
				return "bg-gray-100 text-gray-800";
			default:
				return "bg-gray-100 text-gray-800";
		}
	};

	const getStatusIcon = (status: string) => {
		switch (status) {
			case "completed":
				return CheckCircle;
			case "accepted":
				return CheckCircle;
			case "pending":
				return AlertCircle;
			case "declined":
				return XCircle;
			case "cancelled":
				return XCircle;
			default:
				return AlertCircle;
		}
	};

	const canJoinMeeting = (appointmentDate: string) => {
		const appointmentTime = new Date(appointmentDate);
		const now = new Date();
		const tenMinutesBefore = new Date(
			appointmentTime.getTime() - 10 * 60 * 1000,
		);
		return now >= tenMinutesBefore;
	};

	const handleJoinMeeting = (appointment: Appointment) => {
		setSelectedAppointment(appointment);
		setShowVirtualMeeting(true);
	};

	const handleMessageDoula = (appointment: Appointment) => {
		navigate("/parent-messages", {
			state: {
				doulaUserId: appointment.doula_user_id,
				appointmentId: appointment.id,
			},
		});
	};

	if (loading) {
		return (
			<div className="min-h-screen bg-gray-50 flex items-center justify-center">
				<div className="text-center">
					<div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mx-auto mb-4"></div>
					<p className="text-gray-600 font-karla">Loading your dashboard...</p>
				</div>
			</div>
		);
	}

	return (
		<div className="min-h-screen bg-gray-50">
			<div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
				{/* Top Section: Pregnancy Progress and Due Date Cards */}
				<motion.div
					initial={{ opacity: 0, y: 20 }}
					animate={{ opacity: 1, y: 0 }}
					transition={{ duration: 0.6 }}
					className="mb-8"
				>
					<div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
						{/* Pregnancy Progress Card */}
						{dashboardData.weeksPregnant > 0 && (
							<motion.div
								initial={{ opacity: 0, x: -20 }}
								animate={{ opacity: 1, x: 0 }}
								transition={{ duration: 0.6, delay: 0.1 }}
							>
								<Card className="glass-card border-0 shadow-lg h-full">
									<CardHeader>
										<CardTitle className="flex items-center text-xl font-karla">
											<Baby className="h-6 w-6 mr-2 text-primary-600" />
											Pregnancy Progress
										</CardTitle>
									</CardHeader>
									<CardContent>
										<div className="space-y-4">
											<div className="flex items-center justify-between">
												<span className="text-sm text-gray-600 font-karla">
													Week {dashboardData.weeksPregnant} of 40
												</span>
												<span className="text-sm font-semibold text-primary-600 font-karla">
													{dashboardData.weeksRemaining} weeks to go
												</span>
											</div>
											<div className="relative">
												<div className="w-full bg-gray-200 rounded-full h-3">
													<div
														className="bg-primary-500 h-3 rounded-full transition-all duration-300"
														style={{ width: `${calculateProgress()}%` }}
													></div>
												</div>
											</div>
											<div className="flex items-center justify-between text-sm text-gray-600">
												<span className="font-karla">First Trimester</span>
												<span className="font-karla">Second Trimester</span>
												<span className="font-karla">Third Trimester</span>
											</div>
										</div>
									</CardContent>
								</Card>
							</motion.div>
						)}

						{/* Due Date Card */}
						{dashboardData.parentProfile?.due_date && (
							<motion.div
								initial={{ opacity: 0, x: 20 }}
								animate={{ opacity: 1, x: 0 }}
								transition={{ duration: 0.6, delay: 0.2 }}
							>
								<Card className="glass-card border-0 shadow-lg bg-gradient-to-br from-primary-50 to-primary-100 border-primary-200 h-full">
									<CardHeader className="relative p-4 pb-2">
										<Button
											onClick={handleDueDateEdit}
											variant="ghost"
											size="sm"
											className="absolute top-2 right-2 h-8 w-8 p-0 text-primary-600 hover:bg-primary-200"
										>
											<Pencil className="h-4 w-4" />
										</Button>
									</CardHeader>
									<CardContent className="p-6 pt-0">
										<div className="text-center">
											<div className="flex items-center justify-center mb-3">
												<Baby className="h-8 w-8 text-primary-600 mr-2" />
												<h3 className="text-xl font-bold text-primary-800 font-karla">
													Due Date
												</h3>
											</div>
											<p className="text-2xl font-bold text-primary-900 font-karla mb-2">
												{formatDueDate(dashboardData.parentProfile.due_date)}
											</p>
											<p className="text-sm text-primary-700 font-karla">
												{dashboardData.weeksRemaining} weeks remaining
											</p>
										</div>
									</CardContent>
								</Card>
							</motion.div>
						)}
					</div>
				</motion.div>

				{/* Quick Stats */}
				<motion.div
					initial={{ opacity: 0, y: 20 }}
					animate={{ opacity: 1, y: 0 }}
					transition={{ duration: 0.6, delay: 0.2 }}
					className="mb-8"
				>
					<div className="grid grid-cols-2 md:grid-cols-4 gap-4">
						<Card className="glass-card border-0 shadow-lg">
							<CardContent className="p-4">
								<div className="flex items-center">
									<Heart className="h-8 w-8 text-red-500 mr-3" />
									<div>
										<p className="text-2xl font-bold text-gray-900 font-karla">
											{dashboardData.favoritesCount}
										</p>
										<p className="text-sm text-gray-600 font-karla">
											Favorites
										</p>
									</div>
								</div>
							</CardContent>
						</Card>

						<Card className="glass-card border-0 shadow-lg">
							<CardContent className="p-4">
								<div className="flex items-center">
									<MessageSquare className="h-8 w-8 text-blue-500 mr-3" />
									<div>
										<p className="text-2xl font-bold text-gray-900 font-karla">
											{dashboardData.conversationsCount}
										</p>
										<p className="text-sm text-gray-600 font-karla">
											Conversations
										</p>
									</div>
								</div>
							</CardContent>
						</Card>

						<Card className="glass-card border-0 shadow-lg">
							<CardContent className="p-4">
								<div className="flex items-center">
									<Calendar className="h-8 w-8 text-green-500 mr-3" />
									<div>
										<p className="text-2xl font-bold text-gray-900 font-karla">
											{dashboardData.appointmentsCount}
										</p>
										<p className="text-sm text-gray-600 font-karla">
											Appointments
										</p>
									</div>
								</div>
							</CardContent>
						</Card>

						<Card className="glass-card border-0 shadow-lg">
							<CardContent className="p-4">
								<div className="flex items-center">
									<DollarSign className="h-8 w-8 text-purple-500 mr-3" />
									<div>
										<p className="text-2xl font-bold text-gray-900 font-karla">
											{dashboardData.transactions.length}
										</p>
										<p className="text-sm text-gray-600 font-karla">
											Transactions
										</p>
									</div>
								</div>
							</CardContent>
						</Card>
					</div>
				</motion.div>

				{/* Welcome Section */}
				<motion.div
					initial={{ opacity: 0, y: 20 }}
					animate={{ opacity: 1, y: 0 }}
					transition={{ duration: 0.6, delay: 0.3 }}
					className="mb-8"
				>
					<div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
						{/* Welcome Card */}
						<motion.div
							initial={{ opacity: 0, x: -20 }}
							animate={{ opacity: 1, x: 0 }}
							transition={{ duration: 0.6, delay: 0.1 }}
							className="lg:col-span-2"
						>
							<Card className="glass-card border-0 shadow-lg bg-gradient-to-br from-primary-50 to-primary-100 border-primary-200">
								<CardContent className="p-6">
									<div className="flex items-center space-x-4">
										<div className="flex-shrink-0">
											<UserCircle className="h-16 w-16 text-primary-600" />
										</div>
										<div className="flex-1">
											<h1 className="text-2xl font-bold text-primary-900 font-karla">
												Welcome back, {userProfile?.full_name || "Parent"}!
											</h1>
											<p className="text-primary-700 font-karla mt-1">
												{dashboardData.weeksPregnant > 0
													? `You're ${dashboardData.weeksPregnant} weeks pregnant with ${dashboardData.weeksRemaining} weeks to go!`
													: "Start your doula journey by setting up your profile and finding the perfect doula for you."}
											</p>
										</div>
									</div>
								</CardContent>
							</Card>
						</motion.div>

						{/* Profile Information Card */}
						<motion.div
							initial={{ opacity: 0, x: 20 }}
							animate={{ opacity: 1, x: 0 }}
							transition={{ duration: 0.6, delay: 0.2 }}
						>
							<Card className="glass-card border-0 shadow-lg h-full">
								<CardContent className="p-6">
									<div className="flex items-center mb-4">
										<User className="h-6 w-6 text-primary-600 mr-2" />
										<h3 className="text-lg font-semibold text-gray-900 font-karla">
											Profile
										</h3>
									</div>
									<div className="space-y-3">
										<div>
											<p className="text-xs text-gray-500 font-karla uppercase tracking-wide">
												Full Name
											</p>
											<p className="text-sm font-medium text-gray-900 font-karla">
												{userProfile?.full_name || "Not set"}
											</p>
										</div>
										<div>
											<p className="text-xs text-gray-500 font-karla uppercase tracking-wide">
												Email
											</p>
											<p className="text-sm font-medium text-gray-900 font-karla">
												{userProfile?.email || "Not set"}
											</p>
										</div>
										<div>
											<p className="text-xs text-gray-500 font-karla uppercase tracking-wide">
												Birth Location
											</p>
											<p className="text-sm font-medium text-gray-900 font-karla">
												{dashboardData.parentProfile?.birth_location ||
													(dashboardData.parentProfile?.birth_location_city &&
													dashboardData.parentProfile?.birth_location_state
														? `${dashboardData.parentProfile.birth_location_city}, ${dashboardData.parentProfile.birth_location_state}`
														: "Not set")}
											</p>
										</div>
									</div>
								</CardContent>
							</Card>
						</motion.div>
					</div>
				</motion.div>

				{/* Appointments Section */}
				<motion.div
					initial={{ opacity: 0, y: 20 }}
					animate={{ opacity: 1, y: 0 }}
					transition={{ duration: 0.6, delay: 0.4 }}
					className="mb-8"
				>
					<div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
						<div className="lg:col-span-2">
							<div className="flex items-center justify-between mb-6">
								<h2 className="text-2xl font-bold text-gray-900 font-karla">
									Upcoming Appointments
								</h2>
								<Button
									onClick={() => navigate("/search-doulas")}
									className="bg-primary-600 hover:bg-primary-700 font-karla"
								>
									<Search className="h-4 w-4 mr-2" />
									Find Doulas
								</Button>
							</div>

							{dashboardData.appointments.length === 0 ? (
								<Card className="glass-card border-0 shadow-lg">
									<CardContent className="p-8 text-center">
										<Calendar className="h-12 w-12 text-gray-400 mx-auto mb-4" />
										<h3 className="text-lg font-semibold text-gray-900 font-karla mb-2">
											No Appointments Yet
										</h3>
										<p className="text-gray-600 font-karla mb-4">
											Start by finding and booking appointments with doulas in
											your area.
										</p>
										<Button
											onClick={() => navigate("/search-doulas")}
											className="bg-primary-600 hover:bg-primary-700 font-karla"
										>
											<Search className="h-4 w-4 mr-2" />
											Search Doulas
										</Button>
									</CardContent>
								</Card>
							) : (
								<div className="space-y-4">
									{dashboardData.appointments.map((appointment) => {
										const StatusIcon = getStatusIcon(appointment.status);
										const canJoin = canJoinMeeting(
											appointment.appointment_date,
										);

										return (
											<motion.div
												key={appointment.id}
												initial={{ opacity: 0, y: 10 }}
												animate={{ opacity: 1, y: 0 }}
												transition={{ delay: 0.1 }}
											>
												<Card className="glass-card border-0 shadow-lg hover:shadow-xl transition-all duration-300">
													<CardContent className="p-6">
														{/* Header with doula info and status */}
														<div className="flex items-start justify-between mb-4">
															<div className="flex items-center space-x-3">
																<img
																	src={
																		appointment.doula_user?.avatar_url ||
																		"/placeholder.svg"
																	}
																	alt={
																		appointment.doula_user?.full_name || "Doula"
																	}
																	className="w-12 h-12 rounded-lg object-cover"
																/>
																<div>
																	<h4 className="font-semibold text-gray-900 font-karla">
																		{appointment.doula_user?.full_name ||
																			"Doula"}
																	</h4>
																	<p className="text-sm text-gray-600 font-karla">
																		{appointment.doula_listing?.title ||
																			"Doula Service"}
																	</p>
																</div>
															</div>
															<div className="flex items-center space-x-2">
																<StatusIcon className="h-4 w-4 text-gray-400" />
																<span
																	className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(appointment.status)}`}
																>
																	{appointment.status}
																</span>
															</div>
														</div>

														{/* Appointment details */}
														<div className="space-y-3 mb-4">
															<div className="flex items-center space-x-3 text-sm text-gray-600">
																<Calendar className="h-4 w-4" />
																<span className="font-karla">
																	{formatAppointmentDate(
																		appointment.appointment_date,
																	)}
																</span>
															</div>
															<div className="flex items-center space-x-3 text-sm text-gray-600">
																<Clock className="h-4 w-4" />
																<span className="font-karla">
																	{formatAppointmentTime(
																		appointment.appointment_date,
																	)}{" "}
																	({appointment.duration_minutes} min)
																</span>
															</div>
															<div className="flex items-center space-x-3 text-sm text-gray-600">
																<span className="font-karla">
																	{getTimeUntilAppointment(
																		appointment.appointment_date,
																	)}
																</span>
															</div>
														</div>

														{/* Action buttons */}
														<div className="flex items-center space-x-3 pt-4 border-t border-gray-100">
															<Button
																onClick={() => handleMessageDoula(appointment)}
																variant="outline"
																className="flex-1 border-primary-200 text-primary-700 hover:bg-primary-50 font-karla"
															>
																Message Doula
															</Button>

															{appointment.meeting_link && (
																<Button
																	onClick={() => handleJoinMeeting(appointment)}
																	disabled={!canJoin}
																	className={`flex-1 font-karla ${
																		canJoin
																			? "bg-primary-600 hover:bg-primary-700 text-white shadow-lg"
																			: "bg-primary-200 text-primary-600 cursor-not-allowed"
																	}`}
																>
																	<Video className="h-4 w-4 mr-2" />
																	Join Meeting
																</Button>
															)}
														</div>

														{/* Meeting link display */}
														{appointment.meeting_link && (
															<div className="pt-3 border-t border-gray-100">
																<div className="flex items-center justify-between text-xs text-gray-500 font-karla">
																	<div className="flex items-center space-x-2">
																		<Video className="h-3 w-3" />
																		<a
																			href={appointment.meeting_link}
																			target="_blank"
																			rel="noopener noreferrer"
																			className="truncate text-primary-600 hover:underline"
																		>
																			{appointment.meeting_link}
																		</a>
																	</div>
																</div>
															</div>
														)}
													</CardContent>
												</Card>
											</motion.div>
										);
									})}
								</div>
							)}
						</div>
						{/* Empty third column for spacing */}
						<div className="hidden lg:block"></div>
					</div>
				</motion.div>

				{/* Virtual Meeting Modal */}
				{showVirtualMeeting && selectedAppointment && (
					<VirtualMeeting
						appointment={{
							id: selectedAppointment.id,
							appointment_date: selectedAppointment.appointment_date,
							meeting_link: selectedAppointment.meeting_link || "",
							meeting_id: selectedAppointment.meeting_id || "",
							status: selectedAppointment.status,
							notes: selectedAppointment.notes,
							doula: {
								full_name: selectedAppointment.doula_user?.full_name || "",
								email: selectedAppointment.doula_user?.email || "",
							},
							parent: {
								full_name: userProfile?.full_name || "",
								email: userProfile?.email || "",
							},
						}}
						onClose={() => {
							setShowVirtualMeeting(false);
							setSelectedAppointment(null);
						}}
					/>
				)}

				{/* Edit Due Date Modal */}
				<Dialog open={showEditDueDate} onOpenChange={setShowEditDueDate}>
					<DialogContent className="sm:max-w-md bg-white border border-gray-200 shadow-xl">
						<DialogHeader>
							<DialogTitle className="flex items-center font-karla">
								<Baby className="h-5 w-5 mr-2 text-primary-600" />
								Edit Due Date
							</DialogTitle>
							<DialogDescription className="text-gray-600 font-karla">
								Select a new due date for your pregnancy. This will update your
								progress tracking.
							</DialogDescription>
						</DialogHeader>
						<div className="space-y-4 pt-4">
							<div className="space-y-2">
								<Label htmlFor="due-date" className="font-karla">
									Due Date
								</Label>
								<div className="relative" ref={datePickerRef}>
									<button
										type="button"
										onClick={() => setShowDatePicker(!showDatePicker)}
										className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-primary-500 transition-colors z-10"
									>
										<Calendar className="h-5 w-5" />
									</button>
									<div
										onClick={() => setShowDatePicker(!showDatePicker)}
										className="w-full h-12 pl-10 pr-4 border border-gray-300 rounded-md shadow-sm bg-white flex items-center cursor-pointer hover:border-primary-300 transition-colors text-base"
									>
										<span
											className={newDueDate ? "text-gray-900" : "text-gray-500"}
										>
											{formatDisplayDate(newDueDate)}
										</span>
									</div>

									{/* Custom Date Picker */}
									{showDatePicker && (
										<motion.div
											initial={{ opacity: 0, y: -10 }}
											animate={{ opacity: 1, y: 0 }}
											exit={{ opacity: 0, y: -10 }}
											className="absolute top-full left-0 mt-2 w-80 bg-white border border-gray-200 rounded-xl shadow-2xl z-50 p-6"
										>
											{/* Header */}
											<div className="flex items-center justify-between mb-4">
												<button
													onClick={() => navigateMonth("prev")}
													className="p-2 hover:bg-primary-50 rounded-lg transition-colors"
												>
													<ChevronLeft className="h-5 w-5 text-gray-600" />
												</button>
												<h3 className="text-lg font-semibold text-gray-900">
													{currentDate.toLocaleDateString("en-US", {
														month: "long",
														year: "numeric",
													})}
												</h3>
												<button
													onClick={() => navigateMonth("next")}
													className="p-2 hover:bg-primary-50 rounded-lg transition-colors"
												>
													<ChevronRight className="h-5 w-5 text-gray-600" />
												</button>
											</div>

											{/* Days of Week */}
											<div className="grid grid-cols-7 gap-1 mb-2">
												{["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"].map(
													(day) => (
														<div
															key={day}
															className="text-center text-sm font-medium text-gray-500 py-2"
														>
															{day}
														</div>
													),
												)}
											</div>

											{/* Calendar Days */}
											<div className="grid grid-cols-7 gap-1">
												{/* Create a complete calendar grid */}
												{Array.from({ length: 42 }).map((_, index) => {
													const dayOfWeek = index % 7;
													const weekNumber = Math.floor(index / 7);
													const firstDayOfMonth =
														getFirstDayOfMonth(currentDate);
													const daysInMonth = getDaysInMonth(currentDate);

													// Calculate the actual day number
													const dayNumber = index - firstDayOfMonth + 1;

													// Check if this cell should show a day number
													const isValidDay =
														dayNumber >= 1 && dayNumber <= daysInMonth;

													if (!isValidDay) {
														return (
															<div
																key={`empty-${index}`}
																className="h-10 w-10"
															></div>
														);
													}

													const isSelected =
														newDueDate &&
														new Date(newDueDate).toDateString() ===
															new Date(
																currentDate.getFullYear(),
																currentDate.getMonth(),
																dayNumber,
															).toDateString();
													const isToday =
														new Date().toDateString() ===
														new Date(
															currentDate.getFullYear(),
															currentDate.getMonth(),
															dayNumber,
														).toDateString();

													return (
														<div
															key={`day-${dayNumber}`}
															className="flex items-center justify-center h-10 w-10"
														>
															<button
																onClick={(e) => {
																	e.preventDefault();
																	e.stopPropagation();
																	handleDateSelect(dayNumber);
																}}
																className={`h-full w-full rounded-lg text-sm font-medium transition-all duration-200 flex items-center justify-center ${
																	isSelected
																		? "bg-primary-500 text-white shadow-lg"
																		: isToday
																			? "bg-primary-100 text-primary-700 border-2 border-primary-300"
																			: "hover:bg-primary-50 text-gray-700 hover:text-primary-700"
																}`}
																title={`Select ${dayNumber}`}
															>
																{dayNumber}
															</button>
														</div>
													);
												})}
											</div>

											{/* Today Button */}
											<div className="mt-4 pt-4 border-t border-gray-200">
												<button
													onClick={() => {
														const today = new Date();
														setNewDueDate(formatISODate(today));
														setCurrentDate(today);
														setShowDatePicker(false);
													}}
													className="w-full py-2 px-4 bg-primary-50 text-primary-700 rounded-lg hover:bg-primary-100 transition-colors font-medium"
												>
													Select Today
												</button>
											</div>
										</motion.div>
									)}
								</div>
							</div>
							<div className="flex space-x-2 pt-4">
								<Button
									onClick={() => {
										setShowEditDueDate(false);
										setShowDatePicker(false);
									}}
									variant="outline"
									className="flex-1 font-karla"
									disabled={savingDueDate}
								>
									Cancel
								</Button>
								<Button
									onClick={handleDueDateSave}
									className="flex-1 bg-primary-600 hover:bg-primary-700 font-karla"
									disabled={savingDueDate || !newDueDate}
								>
									{savingDueDate ? "Saving..." : "Save"}
								</Button>
							</div>
						</div>
					</DialogContent>
				</Dialog>
			</div>
		</div>
	);
};

export default ParentDashboard;
